import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { MobileAuthProvider, useMobileAuth } from "./context/MobileAuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { TierProvider } from "./context/TierContext";
import { ChatProvider } from "./context/ChatContext";
import { MobileChatProvider } from "./context/MobileChatContext";
import { SuperAdminProvider, useSuperAdmin } from "./context/SuperAdminContext";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";
import ChatPanel from "./components/ChatPanel";
import Login from "./pages/Login";
import FreeTrial from "./pages/FreeTrial";
import VerifyOTP from "./pages/VerifyOTP";
import OnboardingWizard from "./pages/OnboardingWizard";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import SetPassword from "./pages/SetPassword";
// Super Admin Pages
import {
  SuperAdminLogin,
  SuperAdminForgotPassword,
  SuperAdminResetPassword,
  SuperAdminForcePasswordChange,
  SuperAdminLayout,
  SuperAdminDashboard,
  TenantsList,
  TenantDetail,
  TenantOnboarding,
  TicketsList,
  TicketDetail,
  AdminsList,
  TwoFactorSetup,
  SystemSettings,
  SessionsManagement,
  RoadmapManagement,
  HelpManagement,
  SuperAdminEmails,
  AuthMigration,
  DemoTenantsManagement
} from "./pages/super-admin";
import Dashboard from "./pages/Dashboard";
import GettingStarted from "./pages/GettingStarted";
import Equipment from "./pages/Equipment";
import Operators from "./pages/Operators";
import Customers from "./pages/Customers";
import Suppliers from "./pages/Suppliers";
import TipsMaterials from "./pages/TipsMaterials";
// Jobs merged into Schedule
import Schedule from "./pages/Schedule";
import MyJobs from "./pages/MyJobs";
import Reports from "./pages/Reports";
import Settings from "./pages/Settings";
import DataManagement from "./pages/DataManagement";
import DataImport from "./pages/DataImport";
import PlanningBoard from "./pages/PlanningBoard";
import Compliance from "./pages/Compliance";
import Forms from "./pages/Forms";
import Dockets from "./pages/Dockets";
import Timesheets from "./pages/Timesheets";
import ProductRoadmap from "./pages/ProductRoadmap";
import ChatPage from "./pages/ChatPage";
import SignupSuccess from "./pages/SignupSuccess";
import HelpCenter from "./pages/HelpCenter";
import Support from "./pages/Support";
import TenantTicketDetail from "./pages/TenantTicketDetail";
import Quotes from "./pages/Quotes";
import QuoteEdit from "./pages/QuoteEdit";
import QuoteView from "./pages/QuoteView";
import QuoteApproval from "./pages/QuoteApproval";
// Mobile Pages (Operator)
import MobileLogin from "./pages/mobile/MobileLogin";
import MobileDashboard from "./pages/mobile/MobileDashboard";
import MobileJobs from "./pages/mobile/MobileJobs";
import MobileJobDetail from "./pages/mobile/MobileJobDetail";
import MobileCreateDocket from "./pages/mobile/MobileCreateDocket";
import MobileTimesheet from "./pages/mobile/MobileTimesheet";
import MobileNotifications from "./pages/mobile/MobileNotifications";
import MobileChat from "./pages/mobile/MobileChat";
import MobileForms from "./pages/mobile/MobileForms";
import MobileFormSubmission from "./pages/mobile/MobileFormSubmission";
import MobileHelp from "./pages/mobile/MobileHelp";
import MobileCompliance from "./pages/mobile/MobileCompliance";
import MobileQuotes from "./pages/mobile/MobileQuotes";
import MobileQuoteDetail from "./pages/mobile/MobileQuoteDetail";
import MobileQuoteEdit from "./pages/mobile/MobileQuoteEdit";
import MobileSettings from "./pages/mobile/MobileSettings";
// Mobile Tenant Pages
import {
  TenantMobileDashboard,
  TenantMobileJobs,
  TenantMobileJobDetail,
  TenantMobileCreateJob,
  TenantMobileChat,
  TenantMobileQuotes,
  TenantMobileQuoteDetail,
  TenantMobileQuoteEdit
} from "./pages/tenant-mobile";
import ForcePasswordChange from "./components/ForcePasswordChange";
import { TourProvider, TourOverlay, SubTourProvider, SubTourOverlay } from "./components/tour";
import { useVersionCheck } from "./hooks/useVersionCheck";
import "./App.css";

const ProtectedRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Mobile Protected Route
const MobileProtectedRoute = ({ children }) => {
  const { operator, loading } = useMobileAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1a1a1a]">
        <div className="animate-pulse text-gray-400">Loading...</div>
      </div>
    );
  }
  
  if (!operator) {
    return <Navigate to="/mobile/login" replace />;
  }
  
  return children;
};

// Mobile Routes Component
const MobileRoutes = () => {
  return (
    <MobileAuthProvider>
      <Routes>
        <Route path="/mobile/login" element={<MobileLogin />} />
        <Route path="/mobile/dashboard" element={
          <MobileProtectedRoute><MobileDashboard /></MobileProtectedRoute>
        } />
        <Route path="/mobile/jobs" element={
          <MobileProtectedRoute><MobileJobs /></MobileProtectedRoute>
        } />
        <Route path="/mobile/jobs/:jobId" element={
          <MobileProtectedRoute><MobileJobDetail /></MobileProtectedRoute>
        } />
        <Route path="/mobile/docket/create/:jobId" element={
          <MobileProtectedRoute><MobileCreateDocket /></MobileProtectedRoute>
        } />
        <Route path="/mobile/timesheet" element={
          <MobileProtectedRoute><MobileTimesheet /></MobileProtectedRoute>
        } />
        <Route path="/mobile/notifications" element={
          <MobileProtectedRoute><MobileNotifications /></MobileProtectedRoute>
        } />
        <Route path="/mobile" element={<Navigate to="/mobile/login" replace />} />
      </Routes>
    </MobileAuthProvider>
  );
};

const AppRoutes = () => {
  const { user, loading, pendingTenantSelection } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  // User is considered "logged in" only if they have a user AND don't need to select a tenant
  const isFullyLoggedIn = user && !pendingTenantSelection;

  return (
    <Routes>
      <Route path="/login" element={isFullyLoggedIn ? <Navigate to="/dashboard" replace /> : <Login />} />
      <Route path="/free-trial" element={user ? <Navigate to="/dashboard" replace /> : <FreeTrial />} />
      <Route path="/verify-email" element={user ? <Navigate to="/dashboard" replace /> : <VerifyOTP />} />
      <Route path="/forgot-password" element={user ? <Navigate to="/dashboard" replace /> : <ForgotPassword />} />
      <Route path="/reset-password" element={user ? <Navigate to="/dashboard" replace /> : <ResetPassword />} />
      <Route path="/set-password" element={user ? <Navigate to="/dashboard" replace /> : <SetPassword />} />
      <Route path="/signup-success" element={<SignupSuccess />} />
      <Route path="/quote-approval/:token" element={<QuoteApproval />} />
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <OnboardingWizard />
        </ProtectedRoute>
      } />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="/getting-started" element={<GettingStarted />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/equipment" element={
          <ProtectedRoute roles={["admin", "dispatcher"]}>
            <Equipment />
          </ProtectedRoute>
        } />
        <Route path="/attachments" element={<Navigate to="/equipment" replace />} />
        <Route path="/operators" element={
          <ProtectedRoute roles={["admin", "dispatcher"]}>
            <Operators />
          </ProtectedRoute>
        } />
        <Route path="/customers" element={
          <ProtectedRoute roles={["admin", "dispatcher"]}>
            <Customers />
          </ProtectedRoute>
        } />
        <Route path="/sites" element={<Navigate to="/customers?tab=sites" replace />} />
        <Route path="/suppliers" element={
          <ProtectedRoute roles={["admin", "dispatcher"]}>
            <Suppliers />
          </ProtectedRoute>
        } />
        <Route path="/tips-materials" element={
          <ProtectedRoute roles={["admin", "dispatcher"]}>
            <TipsMaterials />
          </ProtectedRoute>
        } />
        <Route path="/jobs" element={<Navigate to="/schedule" replace />} />
        <Route path="/schedule" element={
          <ProtectedRoute roles={["admin", "dispatcher"]}>
            <Schedule />
          </ProtectedRoute>
        } />
        <Route path="/my-jobs" element={<MyJobs />} />
        <Route path="/reports" element={
          <ProtectedRoute roles={["admin", "dispatcher"]}>
            <Reports />
          </ProtectedRoute>
        } />
        <Route path="/dockets" element={
          <ProtectedRoute roles={["admin", "dispatcher"]}>
            <Dockets />
          </ProtectedRoute>
        } />
        <Route path="/quotes" element={
          <ProtectedRoute roles={["admin", "dispatcher"]}>
            <Quotes />
          </ProtectedRoute>
        } />
        <Route path="/quotes/new" element={
          <ProtectedRoute roles={["admin", "dispatcher"]}>
            <QuoteEdit />
          </ProtectedRoute>
        } />
        <Route path="/quotes/:id" element={
          <ProtectedRoute roles={["admin", "dispatcher"]}>
            <QuoteView />
          </ProtectedRoute>
        } />
        <Route path="/quotes/:id/edit" element={
          <ProtectedRoute roles={["admin", "dispatcher"]}>
            <QuoteEdit />
          </ProtectedRoute>
        } />
        <Route path="/timesheets" element={
          <ProtectedRoute roles={["admin", "dispatcher"]}>
            <Timesheets />
          </ProtectedRoute>
        } />
        <Route path="/planning-board" element={
          <ProtectedRoute roles={["admin", "dispatcher"]}>
            <PlanningBoard />
          </ProtectedRoute>
        } />
        <Route path="/compliance" element={
          <ProtectedRoute roles={["admin", "dispatcher"]}>
            <Compliance />
          </ProtectedRoute>
        } />
        <Route path="/forms" element={
          <ProtectedRoute roles={["admin", "dispatcher"]}>
            <Forms />
          </ProtectedRoute>
        } />
        <Route path="/chat" element={
          <ProtectedRoute roles={["admin", "dispatcher"]}>
            <ChatPage />
          </ProtectedRoute>
        } />
        <Route path="/chat/:roomId" element={
          <ProtectedRoute roles={["admin", "dispatcher"]}>
            <ChatPage />
          </ProtectedRoute>
        } />
        <Route path="/data-management" element={
          <ProtectedRoute roles={["admin"]}>
            <DataManagement />
          </ProtectedRoute>
        } />
        <Route path="/data-import" element={
          <ProtectedRoute roles={["admin"]}>
            <DataImport />
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute roles={["admin"]}>
            <Settings />
          </ProtectedRoute>
        } />
        <Route path="/product-roadmap" element={
          <ProtectedRoute roles={["admin", "dispatcher"]}>
            <ProductRoadmap />
          </ProtectedRoute>
        } />
        <Route path="/help" element={
          <ProtectedRoute>
            <HelpCenter />
          </ProtectedRoute>
        } />
        <Route path="/support" element={
          <ProtectedRoute>
            <Support />
          </ProtectedRoute>
        } />
        <Route path="/support/tickets/:ticketId" element={
          <ProtectedRoute>
            <TenantTicketDetail />
          </ProtectedRoute>
        } />
        <Route path="/users" element={<Navigate to="/settings" replace />} />
      </Route>
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AppRouter />
      </ThemeProvider>
    </BrowserRouter>
  );
}

// Router component that decides between mobile, super-admin, tenant-mobile, and desktop
const AppRouter = () => {
  const location = useLocation();
  const isMobileRoute = location.pathname.startsWith('/mobile');
  const isTenantMobileRoute = location.pathname.startsWith('/tenant-mobile');
  const isSuperAdminRoute = location.pathname.startsWith('/super-admin');

  // Check for new app versions and auto-refresh
  useVersionCheck();

  if (isSuperAdminRoute) {
    return (
      <SuperAdminProvider>
        <SuperAdminRoutes />
        <Toaster position="top-right" richColors />
      </SuperAdminProvider>
    );
  }

  if (isMobileRoute) {
    return (
      <MobileAuthProvider>
        <MobileAppWithLanguage />
        <Toaster position="top-center" richColors />
      </MobileAuthProvider>
    );
  }

  // Tenant Mobile Portal - uses regular auth context
  if (isTenantMobileRoute) {
    return (
      <AuthProvider>
        <TenantMobileAppWithLanguage />
        <Toaster position="top-center" richColors />
      </AuthProvider>
    );
  }

  return (
    <AuthProvider>
      <DesktopAppWithLanguage />
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
};

// Super Admin Routes
const SuperAdminRoutes = () => {
  const { admin, loading, mustChangePassword, tempToken, completePasswordChange } = useSuperAdmin();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ffd108]"></div>
      </div>
    );
  }

  // Intercept if password change is required
  if (admin && mustChangePassword) {
    return (
      <SuperAdminForcePasswordChange
        token={tempToken}
        admin={admin}
        onPasswordChanged={completePasswordChange}
      />
    );
  }

  return (
    <Routes>
      <Route path="/super-admin/login" element={
        admin ? <Navigate to="/super-admin/dashboard" replace /> : <SuperAdminLogin />
      } />
      <Route path="/super-admin/forgot-password" element={
        admin ? <Navigate to="/super-admin/dashboard" replace /> : <SuperAdminForgotPassword />
      } />
      <Route path="/super-admin/reset-password" element={
        admin ? <Navigate to="/super-admin/dashboard" replace /> : <SuperAdminResetPassword />
      } />
      <Route path="/super-admin" element={
        admin ? <SuperAdminLayout /> : <Navigate to="/super-admin/login" replace />
      }>
        <Route index element={<Navigate to="/super-admin/dashboard" replace />} />
        <Route path="dashboard" element={<SuperAdminDashboard />} />
        <Route path="tenants" element={<TenantsList />} />
        <Route path="tenants/new" element={<TenantOnboarding />} />
        <Route path="tenants/:clientId" element={<TenantDetail />} />
        <Route path="demo-tenants" element={<DemoTenantsManagement />} />
        <Route path="tickets" element={<TicketsList />} />
        <Route path="tickets/:ticketId" element={<TicketDetail />} />
        <Route path="sessions" element={<SessionsManagement />} />
        <Route path="roadmap" element={<RoadmapManagement />} />
        <Route path="help" element={<HelpManagement />} />
        <Route path="emails" element={<SuperAdminEmails />} />
        <Route path="agents" element={<AdminsList />} />
        <Route path="security" element={<TwoFactorSetup />} />
        <Route path="settings" element={<SystemSettings />} />
        <Route path="auth-migration" element={<AuthMigration />} />
      </Route>
      <Route path="/super-admin/*" element={<Navigate to="/super-admin/login" replace />} />
    </Routes>
  );
};

// Desktop app with language provider
const DesktopAppWithLanguage = () => {
  const { user, token, mustChangePassword, clearMustChangePassword } = useAuth();
  
  // Show force password change screen if user must change password
  if (user && mustChangePassword) {
    return (
      <ForcePasswordChange 
        token={token} 
        user={user} 
        onPasswordChanged={clearMustChangePassword} 
      />
    );
  }
  
  return (
    <LanguageProvider 
      userType="admin" 
      userId={user?.id} 
      token={token}
    >
      <TierProvider>
        <ChatProvider>
          <TourProvider>
            <SubTourProvider>
              <AppRoutes />
              <ChatPanel />
              <TourOverlay />
              <SubTourOverlay />
            </SubTourProvider>
          </TourProvider>
        </ChatProvider>
      </TierProvider>
    </LanguageProvider>
  );
};

// Mobile app with language provider
const MobileAppWithLanguage = () => {
  const { operator, token } = useMobileAuth();
  
  return (
    <LanguageProvider 
      userType="operator" 
      userId={operator?.id} 
      token={token}
    >
      <MobileChatProvider>
        <MobileAppRoutes />
      </MobileChatProvider>
    </LanguageProvider>
  );
};

// Mobile feature gate component
const MobileFeatureGate = ({ feature, children }) => {
  const { operator, hasFeature } = useMobileAuth();
  
  if (!operator) {
    return <Navigate to="/mobile/login" replace />;
  }
  
  if (!hasFeature(feature)) {
    return <Navigate to="/mobile/dashboard" replace />;
  }
  
  return children;
};

// Separate component for mobile routes
const MobileAppRoutes = () => {
  const { operator, loading, hasFeature } = useMobileAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1a1a1a]">
        <div className="animate-pulse text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/mobile/login" element={operator ? <Navigate to="/mobile/dashboard" replace /> : <MobileLogin />} />
      <Route path="/mobile/dashboard" element={operator ? <MobileDashboard /> : <Navigate to="/mobile/login" replace />} />
      <Route path="/mobile/settings" element={operator ? <MobileSettings /> : <Navigate to="/mobile/login" replace />} />
      <Route path="/mobile/jobs" element={operator ? <MobileJobs /> : <Navigate to="/mobile/login" replace />} />
      <Route path="/mobile/jobs/:jobId" element={operator ? <MobileJobDetail /> : <Navigate to="/mobile/login" replace />} />
      <Route path="/mobile/docket/create/:jobId" element={operator ? <MobileCreateDocket /> : <Navigate to="/mobile/login" replace />} />
      <Route path="/mobile/timesheet" element={
        <MobileFeatureGate feature="timesheets">
          <MobileTimesheet />
        </MobileFeatureGate>
      } />
      <Route path="/mobile/notifications" element={operator ? <MobileNotifications /> : <Navigate to="/mobile/login" replace />} />
      <Route path="/mobile/chat" element={
        <MobileFeatureGate feature="messaging">
          <MobileChat />
        </MobileFeatureGate>
      } />
      <Route path="/mobile/chat/:roomId" element={
        <MobileFeatureGate feature="messaging">
          <MobileChat />
        </MobileFeatureGate>
      } />
      <Route path="/mobile/forms" element={
        <MobileFeatureGate feature="forms">
          <MobileForms />
        </MobileFeatureGate>
      } />
      <Route path="/mobile/forms/submit" element={
        <MobileFeatureGate feature="forms">
          <MobileFormSubmission />
        </MobileFeatureGate>
      } />
      <Route path="/mobile/help" element={operator ? <MobileHelp /> : <Navigate to="/mobile/login" replace />} />
      <Route path="/mobile/compliance" element={
        <MobileFeatureGate feature="compliance">
          <MobileCompliance />
        </MobileFeatureGate>
      } />
      <Route path="/mobile/quotes" element={
        operator && operator.mobile_quotes_enabled ? <MobileQuotes /> : <Navigate to="/mobile/dashboard" replace />
      } />
      <Route path="/mobile/quotes/new" element={
        operator && operator.mobile_quotes_enabled ? <MobileQuoteEdit /> : <Navigate to="/mobile/dashboard" replace />
      } />
      <Route path="/mobile/quotes/:id" element={
        operator && operator.mobile_quotes_enabled ? <MobileQuoteDetail /> : <Navigate to="/mobile/dashboard" replace />
      } />
      <Route path="/mobile/quotes/:id/edit" element={
        operator && operator.mobile_quotes_enabled ? <MobileQuoteEdit /> : <Navigate to="/mobile/dashboard" replace />
      } />
      <Route path="/mobile" element={<Navigate to="/mobile/login" replace />} />
      <Route path="/mobile/*" element={<Navigate to="/mobile/login" replace />} />
    </Routes>
  );
};

// Tenant Mobile App with language provider
const TenantMobileAppWithLanguage = () => {
  const { user, token } = useAuth();
  
  return (
    <LanguageProvider 
      userType="admin" 
      userId={user?.id} 
      token={token}
    >
      <TierProvider>
        <ChatProvider>
          <TenantMobileAppRoutes />
        </ChatProvider>
      </TierProvider>
    </LanguageProvider>
  );
};

// Tenant Mobile Routes - uses regular auth, not operator auth
const TenantMobileAppRoutes = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1a1a1a]">
        <div className="animate-pulse text-gray-400">Loading...</div>
      </div>
    );
  }

  // If not logged in, redirect to regular login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <Routes>
      <Route path="/tenant-mobile" element={<TenantMobileDashboard />} />
      <Route path="/tenant-mobile/jobs" element={<TenantMobileJobs />} />
      <Route path="/tenant-mobile/jobs/new" element={<TenantMobileCreateJob />} />
      <Route path="/tenant-mobile/jobs/:jobId" element={<TenantMobileJobDetail />} />
      <Route path="/tenant-mobile/quotes" element={<TenantMobileQuotes />} />
      <Route path="/tenant-mobile/quotes/new" element={<TenantMobileQuoteEdit />} />
      <Route path="/tenant-mobile/quotes/:id" element={<TenantMobileQuoteDetail />} />
      <Route path="/tenant-mobile/quotes/:id/edit" element={<TenantMobileQuoteEdit />} />
      <Route path="/tenant-mobile/chat" element={<TenantMobileChat />} />
      <Route path="/tenant-mobile/chat/:roomId" element={<TenantMobileChat />} />
      <Route path="/tenant-mobile/*" element={<Navigate to="/tenant-mobile" replace />} />
    </Routes>
  );
};

export default App;
